// Lib
import { createTheme } from '@mui/material';
import { isMobile } from 'react-device-detect';

// Constants
import USER_TYPE from '../constants/user';
import {
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_SEMIBOLD,
} from '../constants/typography';

// Styles
import breakpoints from './breakpoints';

const theme = createTheme();

const bold = {
  fontWeight: FONT_WEIGHT_BOLD,
};

const semibold = {
  fontWeight: FONT_WEIGHT_SEMIBOLD,
};

const textLg = {
  fontSize: 18,
  fontWeight: FONT_WEIGHT_REGULAR,
  lineHeight: '22px',
  letterSpacing: '0.2px',
  [theme.breakpoints.down(breakpoints.values.md)]: {
    fontSize: 16,
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
    ...(isMobile && {
      fontSize: 16,
      lineHeight: '20px',
      letterSpacing: '0.1px',
    }),
  },
};

const textMd = {
  fontSize: 16,
  fontWeight: FONT_WEIGHT_REGULAR,
  lineHeight: '20px',
  letterSpacing: '0.1px',
  [theme.breakpoints.down(breakpoints.values.md)]: {
    fontSize: 14,
    lineHeight: '18px',
  },
  [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
    ...(isMobile && {
      fontSize: 14,
      lineHeight: '18px',
    }),
  },
};

const textSm = (userType) => ({
  fontSize: 14,
  fontWeight: FONT_WEIGHT_REGULAR,
  lineHeight: userType === USER_TYPE.gilead ? '16px' : '20px',
  [theme.breakpoints.down(breakpoints.values.md)]: {
    fontSize: 12,
    lineHeight: '16px',
    letterSpacing: '0.1px',
  },
  [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
    ...(isMobile && {
      fontSize: 12,
      lineHeight: '16px',
      letterSpacing: '0.1px',
    }),
  },
});

const textXs = {
  fontSize: 12,
  fontWeight: FONT_WEIGHT_REGULAR,
  lineHeight: '20px',
  [theme.breakpoints.down(breakpoints.values.md)]: {
    fontSize: 10,
    lineHeight: '12px',
    letterSpacing: '0.1px',
  },
  [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
    ...(isMobile && {
      fontSize: 10,
      lineHeight: '12px',
      letterSpacing: '0.1px',
    }),
  },
};

const createTypography = (userType) => ({
  useNextVariants: true,
  fontFamily:
    userType === USER_TYPE.gilead
      ? 'Proxima Nova, sans-serif'
      : 'Helvetica, sans-serif',
  fontSize: 16,
  body1: {
    fontSize: 16,
    [theme.breakpoints.down(breakpoints.values.md)]: {
      fontSize: 14,
    },
    [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
      ...(isMobile && {
        fontSize: 14,
      }),
    },
  },
  button: {
    fontSize: 16,
    [theme.breakpoints.down(breakpoints.values.md)]: {
      fontSize: 14,
    },
    [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
      ...(isMobile && {
        fontSize: 14,
      }),
    },
  },
  h1: {
    fontSize: 36,
    fontWeight: FONT_WEIGHT_BOLD,
    lineHeight: '44px',
    [theme.breakpoints.down(breakpoints.values.md)]: {
      fontSize: 22,
      lineHeight: '28px',
    },
    [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
      ...(isMobile && {
        fontSize: 22,
        lineHeight: '28px',
      }),
    },
  },
  h2: {
    fontSize: 28,
    fontWeight: FONT_WEIGHT_BOLD,
    lineHeight: '38px',
    [theme.breakpoints.down(breakpoints.values.md)]: {
      fontSize: 16,
      lineHeight: '24px',
    },
    [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
      ...(isMobile && {
        fontSize: 16,
        lineHeight: '24px',
      }),
    },
  },
  h3: {
    fontSize: 18,
    fontWeight: FONT_WEIGHT_BOLD,
    lineHeight: '24px',
    [theme.breakpoints.down(breakpoints.values.md)]: {
      fontSize: 16,
      lineHeight: '20px',
    },
    [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
      ...(isMobile && {
        fontSize: 16,
        lineHeight: '20px',
      }),
    },
  },
  h4: {
    fontSize: 16,
    fontWeight: FONT_WEIGHT_BOLD,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    [theme.breakpoints.down(breakpoints.values.md)]: {
      fontSize: 14,
      lineHeight: '20px',
    },
    [`@media (max-height: 767.85px) and (orientation: landscape)`]: {
      ...(isMobile && {
        fontSize: 14,
        lineHeight: '20px',
      }),
    },
  },
  textLg,
  textLgSemibold: {
    ...textLg,
    ...semibold,
  },
  textLgBold: {
    ...textLg,
    ...bold,
  },
  textMd,
  textMdSemibold: {
    ...textMd,
    ...semibold,
  },
  textMdBold: {
    ...textMd,
    ...bold,
  },
  textSm: textSm(userType),
  textSmSemibold: {
    ...textSm(userType),
    ...semibold,
  },
  textSmBold: {
    ...textSm(userType),
    ...bold,
  },
  textXs,
  textXsSemibold: {
    ...textXs,
    ...semibold,
  },
  textXsBold: {
    ...textXs,
    ...bold,
  },
});

export default createTypography;
